import React from 'react'

import { type IconName, iconMapping } from './iconMapping'

export interface IconProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Icon name - supports both MUI names (e.g., 'Add', 'Search') and Material Symbol names (e.g., 'add', 'search') */
  name: IconName | string
  /** Icon size in pixels (default: 24) */
  size?: number
  /** MUI-style fontSize for compatibility */
  fontSize?: 'inherit' | 'small' | 'medium' | 'large' | string
  /** Font weight (default: 300 for 1.5px stroke) */
  weight?: 100 | 200 | 300 | 400 | 500 | 600 | 700
  /** Fill state (default: 1 for filled, 0 for outlined) */
  fill?: 0 | 1
  /** Grade adjustment for fine-tuning thickness (default: 0) */
  grade?: number
  /** Optical size (default: 24) */
  opticalSize?: number
  /** Custom color */
  color?: string
  /* Show gradient fill */
  gradient?: boolean
}

/**
 * Icon component using Google Material Symbols with customizable font weight
 * Default weight is 300 (1.5px stroke width) for outlined style
 *
 * @example
 * ```tsx
 * <Icon name="Add" />
 * <Icon name="Search" size={32} weight={400} />
 * <Icon name="check_circle" fill={1} color="#00ff00" />
 * ```
 */
export const Icon = React.forwardRef<HTMLSpanElement, IconProps>(
  (
    {
      name,
      size,
      fontSize,
      weight = 300,
      fill,
      grade = 0,
      opticalSize = 24,
      color,
      className,
      style,
      gradient,
      ...props
    },
    ref,
  ) => {
    // Convert MUI icon name to Material Symbol name if needed
    const symbolName = (iconMapping[name as IconName] || name) as string

    const outlinedExceptions = ['ContentCopy', 'CopyAll', 'content_copy', 'copy_all']
    const shouldBeFilled =
      fill !== undefined
        ? fill
        : !name.includes('Outlined') &&
            !name.includes('outline') &&
            !outlinedExceptions.includes(name)
          ? 1
          : 0

    // Handle fontSize prop (MUI compatibility)
    const fontSizeMap = {
      inherit: undefined,
      small: '20px',
      medium: '24px',
      large: '32px',
    }

    const computedFontSize = size
      ? `${size}px`
      : fontSize && fontSize in fontSizeMap
        ? fontSizeMap[fontSize as keyof typeof fontSizeMap]
        : fontSize || '24px'

    return (
      <span
        ref={ref}
        className={`material-symbols-outlined ${className || ''}`}
        style={{
          fontSize: computedFontSize,
          fontVariationSettings: `'FILL' ${shouldBeFilled}, 'wght' ${weight}, 'GRAD' ${grade}, 'opsz' ${opticalSize}`,
          color: gradient ? 'transparent' : color || 'inherit',
          ...(gradient
            ? {
                backgroundImage: 'linear-gradient(to right, #ed1c24, #27aae1)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
              }
            : {}),
          width: size || (fontSize === 'inherit' ? undefined : computedFontSize),
          height: size || (fontSize === 'inherit' ? undefined : computedFontSize),
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          userSelect: 'none',
          ...style,
        }}
        {...props}
      >
        {symbolName}
      </span>
    )
  },
)

Icon.displayName = 'Icon'

export default Icon
